import { useLayoutEffect, useState } from 'react';

import { cn } from '@change/ds-utils';

import {
	ContentSection,
	MainContent,
	ResponsiveNavigation,
	ResponsiveNavigationItem,
	ResponsiveNavigationLayout,
} from '@change/ds-layout/next';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { MainContentTitle, ModuleSeparator } from 'src/app/pages/starterDashboardV2/shared/components';
import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';
import {
	ButtonVariantProvider,
	ReadyToShareProvider,
	RecommendationsProvider,
	SharingModalProvider,
	useDynamicModules,
} from 'src/app/pages/starterDashboardV2/shared/contexts';

import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

import { DecisionMakersList } from './components/DecisionMakersList';
import { RecentSupporters } from './components/EngageSupporters/components/RecentSupporters';
import { GrowPetition } from './components/GrowPetition';
import { NotificationsCenter } from './components/NotificationsCenter';
import { PageBanner } from './components/PageBanner';
import { PetitionInsights } from './components/PetitionInsights';
import { PetitionOverview } from './components/PetitionOverview';
import { ReadyToShare } from './components/ReadyToShare';
import { Recommendations } from './components/Recommendations';
import { StarterDashboardComments } from './components/StarterDashboardComments';
import { StepsToVictory } from './components/StepsToVictory';
import { UpdateSupporters } from './components/UpdateSupporters';
import { NAVIGATION_ITEMS } from './constants';
import { useOneTimeBadge } from './hooks/useOneTimeBadge';

const TAB_CLICK_EVENTS: Record<string, string> = {
	home: HOME_TAB_CLICK,
	share: SHARE_TAB_CLICK,
	'decision-maker': DM_TAB_CLICK,
	updates: SUPPORTERS_TAB_CLICK,
};

// eslint-disable-next-line max-lines-per-function
export function PageBodyReskin() {
	const { slug, section } = useKnownAvailableParams<{ slug: string; section: string }>();
	const baseUrl = `/p/${slug}/dashboard`;
	const track = useTracking();
	const [previous, setPrevious] = useState('');

	const {
		data: { showBadge },
		actions: { markBadgeAsViewed },
	} = useOneTimeBadge('starter_dashboard_share_badge_viewed');

	useLayoutEffect(() => {
		if (!previous || previous === section) return;
		const event = TAB_CLICK_EVENTS[section];
		void track(event, { context: previous });
		window.scrollTo({ top: 0 });

		section === 'share' && showBadge && markBadgeAsViewed();
	}, [section, previous, track, showBadge, markBadgeAsViewed]);

	const { isDynamicModuleLoaded } = useDynamicModules();
	const [commentsVisibility, setCommentsVisibility] = useState(true);

	// Get viewport information for responsive behavior
	const viewportInfo = useResponsiveLayout();

	return (
		<ButtonVariantProvider data-qa="button-variant-context">
			<ReadyToShareProvider>
				<RecommendationsProvider>
					<SharingModalProvider>
						<ResponsiveNavigationLayout
							onTabChange={setPrevious}
							data-testid="page-body-reskin"
							viewportInfo={viewportInfo}
							selectedKey={section}
							defaultSelectedKey="home"
						>
							<ResponsiveNavigation ariaLabel="Main Application Navigation" className="px-0 pb-0 pt-0">
								{NAVIGATION_ITEMS.map((item) => (
									<ResponsiveNavigationItem
										key={item.id}
										selectedIcon={item.selectedIcon}
										icon={item.icon}
										id={item.id}
										desktopLabel={item.desktopLabel}
										mobileLabel={item.mobileLabel}
										ariaLabel={item.ariaLabel}
										href={`${baseUrl}/${item.section}`}
										badge={item.id === 'share' && showBadge ? item.badge : undefined}
										className="shrink-0 pb-3 pt-2.5 data-[selected=true]:shadow-[inset_0_3px_0_0_var(--stroke-info-strong-color)] md:pt-3 md:data-[selected=true]:shadow-none max-md:[&_.relative:has([data-namespace='@change/ds-icons'])]:leading-[0px] max-md:[&_[data-namespace='@change/ds-icons']]:!h-7 max-md:[&_[data-namespace='@change/ds-icons']]:!w-7 max-md:[&_[data-namespace='@change/ds-icons']_svg]:!h-7 max-md:[&_[data-namespace='@change/ds-icons']_svg]:!w-7"
									/>
								))}
							</ResponsiveNavigation>
							<MainContent>
								<ContentSection id="home" className="flex flex-col">
									<PageBanner />
									{/* The following title should only be visible in desktop */}
									<NotificationsCenter />
									<MainContentTitle title="Home" className="lg:hidden" />
									<PetitionOverview />
									<ModuleSeparator />
									<Recommendations viewContext="home" />
									{isDynamicModuleLoaded('Recommendations') && (
										<ModuleSeparator data-testid="petition-overview-separator" />
									)}
									<div className={cn('flex flex-col', { 'lg:flex-row': commentsVisibility })}>
										<div className="flex-1">
											{/* eslint-disable-next-line react/jsx-max-depth */}
											<RecentSupporters />
										</div>
										{commentsVisibility && (
											<>
												<div className="flex-0 min-w-[1px]">
													<ModuleSeparator className="block lg:hidden" />
													<ModuleSeparator orientation="vertical" className="hidden lg:block" />
												</div>
												<div className="flex-1">
													<StarterDashboardComments setCommentsVisibility={setCommentsVisibility} />
												</div>
											</>
										)}
									</div>
									{isDynamicModuleLoaded('PetitionInsights') && (
										<ModuleSeparator data-testid="petition-insights-separator" />
									)}
									<PetitionInsights />
								</ContentSection>

								<ContentSection id="share" className="flex flex-col">
									<MainContentTitle title="Share your petition" />
									<ReadyToShare />
									{isDynamicModuleLoaded('ReadyToShare') && <ModuleSeparator data-testid="ready-to-share-separator" />}
									<Recommendations viewContext="share" />
									{isDynamicModuleLoaded('Recommendations') && (
										<ModuleSeparator data-testid="share-recommendations-separator" />
									)}
									<GrowPetition />
									{isDynamicModuleLoaded('StepsToVictory') && (
										<ModuleSeparator data-testid="steps-to-victory-separator" />
									)}
									<StepsToVictory />
								</ContentSection>

								<ContentSection id="decision-maker" className="flex flex-col">
									<MainContentTitle title="Contact decision makers" />
									<DecisionMakersList />
								</ContentSection>
								<ContentSection id="updates" className="flex flex-col">
									<MainContentTitle title="Post an update" />
									<UpdateSupporters />
								</ContentSection>
							</MainContent>
						</ResponsiveNavigationLayout>
					</SharingModalProvider>
				</RecommendationsProvider>
			</ReadyToShareProvider>
		</ButtonVariantProvider>
	);
}
